import moment from 'moment';
import {
    createSelector,
} from 'reselect';

import {
    regionFromStateAndCountry,
} from 'wanderu-utils';

import {
    selectors as userInfoSelectors,
} from './userInfo';

/**
 * @param {Object} order
 * @param {String} which - `depart` or `arrive`
 * @return {Object}
 */
function _getEndpointInfo(order, which) {
    if (!order || !which) {
        throw new Error('order and direction must be provided');
    }

    const {
        tripDetails,
    } = order;
    const {
        tripId,
    } = tripDetails;
    const {
        address,
        name,
    } = tripDetails[`${which}Station`];
    const dateTime = tripDetails[`${which}DateTime`];
    const dateTimeUTC = tripDetails[`${which}DateTimeUTC`];
    const timezone = tripDetails[`${which}Timezone`];
    const {
        city,
        country,
        stateProvince,
    } = address;

    return {
        cityName: city,
        dateTime: dateTimeUTC,
        localizedDateTime: moment.tz(dateTime, timezone).format(),
        name,
        region: regionFromStateAndCountry(stateProvince, country),
        tripId,
        type: which,
    };
}

/**
 * @function
 * @param {Object} appState
 * @param {String} tripId
 * @param {String} orderId
 * @return {Object}
 */
const selectArriveInfo = createSelector(
    userInfoSelectors.selectTripByIds,
    (purchasedTrip) => {
        if (!purchasedTrip || !purchasedTrip.tripDetails) {
            return;
        }

        return _getEndpointInfo(purchasedTrip, 'arrive');
    },
);

/**
 * @function
 * @param {Object} appState
 * @param {String} tripId
 * @param {String} orderId
 * @return {String}
 */
const selectCarrierCode = createSelector(
    userInfoSelectors.selectTripByIds,
    (purchasedTrip) => {
        if (!purchasedTrip || !purchasedTrip.tripDetails) {
            return;
        }

        return purchasedTrip.tripDetails.carrierId;
    },
);

/**
 * @function
 * @param {Object} appState
 * @param {String} tripId
 * @param {String} orderId
 * @return {String}
 */
const selectCarrierName = createSelector(
    userInfoSelectors.selectTripByIds,
    (purchasedTrip) => {
        if (!purchasedTrip || !purchasedTrip.tripDetails) {
            return;
        }

        return purchasedTrip.tripDetails.carrierName;
    },
);

/**
 * @function
 * @param {Object} appState
 * @param {String} tripId
 * @param {String} orderId
 * @return {Object}
 */
const selectDepartInfo = createSelector(
    userInfoSelectors.selectTripByIds,
    (purchasedTrip) => {
        if (!purchasedTrip || !purchasedTrip.tripDetails) {
            return;
        }

        return _getEndpointInfo(purchasedTrip, 'depart');
    },
);

/**
 * @function
 * @param {Object} appState
 * @param {String} tripId
 * @param {String} orderId
 * @return {Object}
 */
const selectPrice = createSelector(
    userInfoSelectors.selectTripByIds,
    (purchasedTrip) => {
        if (!purchasedTrip || !purchasedTrip.tripDetails) {
            return;
        }

        const {
            totalCharge,
        } = purchasedTrip.tripDetails;
        const {
            amount,
            currency,
            exchangeRate,
            userCurrency,
        } = totalCharge;
        const pricing = {
            [currency]: amount,
        };

        if (userCurrency && exchangeRate) {
            pricing[userCurrency] = amount * exchangeRate;
        }

        return pricing;
    },
);

/**
 * @function
 * @param {Object} appState
 * @param {String} tripId
 * @param {String} orderId
 * @return {String}
 */
const selectVehicleType = createSelector(
    userInfoSelectors.selectTripByIds,
    (purchasedTrip) => {
        if (!purchasedTrip || !purchasedTrip.tripDetails) {
            return;
        }

        return purchasedTrip.tripDetails.vehicleTypes[0];
    },
);

export {
    selectArriveInfo,
    selectCarrierCode,
    selectCarrierName,
    selectDepartInfo,
    selectPrice,
    selectVehicleType,
};
